import React, { useEffect, useState } from 'react';
import { staffApi, trainingApi } from '../../lib/api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Info, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import StaffCombobox from './StaffCombobox';

export const emptyModule = () => ({ module_no: '', types: [], other_type: '', area: '', key_actions: '', hours: '', objectives: ['', '', ''], next_check_date: '' });

export function ModuleEditor({ idx, mod, meta, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...mod, [k]: v });
  const toggleType = (t) => set('types', mod.types.includes(t) ? mod.types.filter(x => x !== t) : [...mod.types, t]);
  return (
    <div className="border border-slate-200 p-3 space-y-3 bg-slate-50/60" data-testid={`module-editor-${idx}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-600">Modulo formativo {idx + 1}</p>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={onRemove} data-testid={`module-remove-${idx}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Tipologia formazione erogata</label>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {meta.types.map(t => (
            <button key={t} type="button" onClick={() => toggleType(t)}
                    className={`px-2.5 py-1 text-xs border transition-colors ${mod.types.includes(t)
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500'}`}
                    data-testid={`module-${idx}-type-${t.toLowerCase().replace(/ /g, '-')}`}>
              {t}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <div className="flex items-center gap-1.5 h-5">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">N° modulo</label>
            <Popover modal={true}>
              <PopoverTrigger asChild>
                <button type="button" className="text-slate-400 hover:text-slate-700 transition-colors"
                        title="Legenda percorso / aree tematiche" data-testid={`module-${idx}-area-info`}>
                  <Info className="w-3.5 h-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-96 max-h-80 overflow-y-auto rounded-none" align="start">
                <p className="font-bold uppercase tracking-wider text-[10px] text-slate-500 mb-2">
                  Percorso — aree tematiche (dal modello cartaceo)
                </p>
                {(meta.areas || []).map((a, i) => (
                  <div key={a.name} className="group py-1 border-b border-slate-100 text-xs cursor-default"
                       data-testid={`legend-area-${i}`}>
                    <div className="flex justify-between gap-3">
                      <span className="text-slate-700">
                        <span className="text-slate-400 tabular-nums mr-1.5">{i + 1}.</span>{a.name}
                      </span>
                      <span className="text-slate-400 whitespace-nowrap">{a.hours}h · {a.category === 'base' ? 'Base' : 'Tecnica'}</span>
                    </div>
                    {a.description && (
                      <p className="hidden group-hover:block text-[11px] text-slate-500 mt-1 leading-snug bg-slate-50 border-l-2 border-slate-300 pl-2 py-1"
                         data-testid={`legend-area-desc-${i}`}>
                        {a.description}
                      </p>
                    )}
                  </div>
                ))}
                <p className="text-[10px] text-slate-400 mt-2">
                  Il n° modulo fa riferimento a questa legenda. Passa il mouse su un'area per la descrizione
                  (modificabile da Impostazioni → Formazione).
                </p>
              </PopoverContent>
            </Popover>
          </div>
          <Input value={mod.module_no || ''} onChange={e => set('module_no', e.target.value)}
                 placeholder="es. 1, 2, 6, 9, Onboarding…" maxLength={120}
                 className="h-8 rounded-none mt-1" data-testid={`module-${idx}-no`} />
        </div>
        {mod.types.includes('Altro') && (
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Specifica "Altro"</label>
            <Input value={mod.other_type || ''} onChange={e => set('other_type', e.target.value)}
                   placeholder="Descrivi la tipologia" maxLength={160}
                   className="h-8 rounded-none mt-1" data-testid={`module-${idx}-other-type`} />
          </div>
        )}
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Azioni chiave e prossimo step</label>
        <Textarea value={mod.key_actions || ''} onChange={e => set('key_actions', e.target.value)}
                  rows={2} maxLength={2000} className="rounded-none mt-1 text-sm" data-testid={`module-${idx}-actions`} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Durata / ore utilizzate</label>
          <Input type="number" min="0" step="0.5" value={mod.hours ?? ''} onChange={e => set('hours', e.target.value)}
                 className="h-8 rounded-none mt-1" data-testid={`module-${idx}-hours`} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Prossima verifica</label>
          <Input type="date" value={mod.next_check_date || ''} onChange={e => set('next_check_date', e.target.value)}
                 className="h-8 rounded-none mt-1" data-testid={`module-${idx}-check`} />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Obiettivi prioritari (max 3)</label>
        {[0, 1, 2].map(i => (
          <Input key={i} value={mod.objectives[i] || ''} placeholder={`Obiettivo ${i + 1}`}
                 onChange={e => {
                   const obj = [...mod.objectives]; obj[i] = e.target.value; set('objectives', obj);
                 }}
                 className="h-8 rounded-none mt-1" data-testid={`module-${idx}-obj-${i}`} />
        ))}
      </div>
    </div>
  );
}

export default function RegisterDialog({ meta, register, onClose, onSaved }) {
  const isEdit = !!register;
  const [staff, setStaff] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => isEdit ? {
    staff_id: register.staff_id,
    tutor_staff_id: register.tutor_staff_id || '',
    tutor_name: register.tutor_name || '',
    start_date: register.start_date || '',
    end_date: register.end_date || '',
    next_check_date: register.next_check_date || '',
    notes: register.notes || '',
    modules: (register.modules || []).map(m => ({ ...m, objectives: [...(m.objectives || []), '', '', ''].slice(0, 3) })),
  } : {
    staff_id: '', tutor_staff_id: '', tutor_name: '',
    start_date: new Date().toISOString().slice(0, 10), end_date: '',
    next_check_date: '', notes: '', modules: [emptyModule()],
  });

  useEffect(() => {
    staffApi.list().then(r => setStaff(r.data)).catch(() => setStaff([]));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!isEdit && !form.staff_id) { toast.error('Seleziona l\'allievo'); return; }
    setSaving(true);
    try {
      const payload = {
        tutor_staff_id: form.tutor_staff_id || null,
        start_date: form.start_date || null, end_date: form.end_date || null,
        notes: form.notes || null,
        tutor_name: form.tutor_name || null,
      };
      if (!isEdit) {
        // i moduli si gestiscono dal dettaglio registro dopo la creazione/modifica
        payload.staff_id = form.staff_id;
        payload.modules = form.modules.map(m => ({
          ...m,
          hours: m.hours === '' || m.hours == null ? null : parseFloat(m.hours),
          objectives: m.objectives.filter(o => (o || '').trim()),
          area: m.area || null, module_no: m.module_no || null,
          other_type: m.types.includes('Altro') ? (m.other_type || null) : null,
          key_actions: m.key_actions || null, next_check_date: m.next_check_date || null,
        }));
      }
      const r = isEdit
        ? await trainingApi.updateRegister(register.register_id, payload)
        : await trainingApi.createRegister(payload);
      toast.success(isEdit ? 'Registro aggiornato' : 'Registro creato');
      onSaved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio fallito');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="register-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifica dati registro' : 'Nuovo registro formativo'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Dati generali del registro — i moduli si modificano dai riquadri nel dettaglio.'
              : 'Registro percorso crescita formativa — stessa impostazione del modello cartaceo.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {!isEdit && (
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Allievo *</label>
              <StaffCombobox staff={staff} value={form.staff_id} onChange={v => set('staff_id', v)}
                             testId="register-staff-select" />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Tutor</label>
            <StaffCombobox staff={staff} value={form.tutor_staff_id}
                           noneLabel="— Nessuno / testo libero —"
                           onChange={v => {
                             const t = staff.find(s => s.staff_id === v);
                             setForm(f => ({ ...f, tutor_staff_id: v, tutor_name: t ? (t.display_name || `${t.first_name} ${t.last_name}`) : f.tutor_name }));
                           }}
                           testId="register-tutor-select" />
          </div>
          {!form.tutor_staff_id && (
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Nome tutor (libero)</label>
              <Input value={form.tutor_name} onChange={e => set('tutor_name', e.target.value)}
                     className="rounded-none mt-1" data-testid="register-tutor-name" />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Inizio programma</label>
            <Input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)}
                   className="rounded-none mt-1" data-testid="register-start-date" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Termine programma</label>
            <Input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)}
                   className="rounded-none mt-1" data-testid="register-end-date" />
          </div>
        </div>

        {!isEdit && (
          <div className="space-y-3">
            {form.modules.map((m, i) => (
              <ModuleEditor key={m.module_id || i} idx={i} mod={m} meta={meta}
                            onChange={nm => set('modules', form.modules.map((x, j) => j === i ? nm : x))}
                            onRemove={() => set('modules', form.modules.filter((_, j) => j !== i))} />
            ))}
            <Button variant="outline" size="sm" className="gap-1.5 rounded-none" onClick={() => set('modules', [...form.modules, emptyModule()])}
                    data-testid="add-module-btn">
              <Plus className="w-3.5 h-3.5" /> Aggiungi modulo
            </Button>
          </div>
        )}

        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Note</label>
          <Textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} maxLength={4000}
                    className="rounded-none mt-1 text-sm" data-testid="register-notes" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="register-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
            {isEdit ? 'Salva modifiche' : 'Crea registro'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

